import React, { useRef, useState } from 'react';
import { Button, Checkbox, Popconfirm } from 'antd';
import {
  Container,
  Content,
  SubTitle,
  Title,
  FeatureBox,
  FeatureTitle,
  FeaturesGrid,
  IconGrid,
  FeatureText,
  TrustBar,
  GartnerBoxText,
  RatingText,
  IconBox,
  LastStep,
  StyledDrawer,
  TitleWrapper,
  ButtonWrapper,
  ProductHighlights,
  DoNotShowButton,
  DoNotShowButtonWrapper,
} from './styled';
import usePageTitle from '@src/hooks/usePageTitle';
import { UserProfileEditor } from '@src/components/user-management/components/UserProfileEditor';
import { requestInviteUsers } from '@src/components/user-management/hooks';
import { useSelector } from 'react-redux';
import { isAdminRoleSelector } from '@store/common/selectors';
import { useUpdateIntegrationStatus } from '@hooks/eco/useSelfServeIntegrationStatus';

function Welcome() {
  usePageTitle({ text: 'Welcome' });

  const isAdmin = useSelector(isAdminRoleSelector);
  const { mutate: updateIntegrationStatus } = useUpdateIntegrationStatus();

  const [visible, setVisible] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [popOpen, setPopOpen] = useState(false);

  const fromPopCancelRef = useRef(false);

  const onCloseInvite = () => {
    setVisible(false);
  };
  const onShowInvite = () => {
    setVisible(true);
  };

  const handleCheckboxClick = () => {
    // Use fromPopCancelRef to prevent pop from being unable to close when clicking the checkbox.
    // Event trigger sequence: handlePopCancel => handleCheckboxClick
    if (fromPopCancelRef.current) {
      fromPopCancelRef.current = false;
      return;
    }

    setIsChecked((prevState) => !prevState);
    setPopOpen((prevState) => !prevState);
  };

  const handleNotShowClick = () => {
    if (isChecked) {
      updateIntegrationStatus({
        integrationStatus: true,
      });
      handlePopCancel();
    }
  };

  const handlePopCancel = () => {
    fromPopCancelRef.current = true;
    setIsChecked(false);
    setPopOpen(false);
  };

  const handlePopOpenChange = (open) => {
    if (!open) {
      handlePopCancel();
    }
  };

  return (
    <Container>
      <Content>
        <TitleWrapper>
          <SubTitle>Welcome to Conviva</SubTitle>
          <Title>Real-time Performance Analytics</Title>
        </TitleWrapper>
        <ButtonWrapper>
          <LastStep>One last step before real-time insights ...</LastStep>
          <Button
            type="primary"
            onClick={() => {
              window.open('/app/dpi-integration', '_self');
            }}
            size={'large'}
            style={{ height: '46px', width: '367px', fontSize: '20px' }}
          >
            Integrate SDK
          </Button>
          {isAdmin ? (
            <Button type="link" onClick={onShowInvite} size={'large'} style={{ padding: '0px' }}>
              Invite Engineering Team
            </Button>
          ) : null}
        </ButtonWrapper>

        <ProductHighlights>
          <TrustBar>
            <div>
              <GartnerBoxText>Gartner Peer</GartnerBoxText>
              <GartnerBoxText style={{ color: '#40B3E0' }}>&nbsp;Insights</GartnerBoxText>
              <GartnerBoxText>™</GartnerBoxText>
            </div>
            <RatingText>4.8</RatingText>
            <IconBox
              src={'https://ca-stg1.viewer.conviva.com/pulse_static/welcome/rating-icon.svg'}
              alt="rating"
              style={{ width: '140px' }}
            />
          </TrustBar>
          <FeaturesGrid>
            <FeatureBox>
              <FeatureTitle>
                Lightweight SDKs
                <br />
                for quick integration.
              </FeatureTitle>
              <IconGrid>
                <IconBox
                  src={'https://ca-stg1.viewer.conviva.com/pulse_static/welcome/ios.png'}
                  alt="ios"
                />
                <IconBox
                  src={'https://ca-stg1.viewer.conviva.com/pulse_static/welcome/android.png'}
                  alt="android"
                />
                <IconBox
                  src={'https://ca-stg1.viewer.conviva.com/pulse_static/welcome/js.png'}
                  alt="web"
                />
              </IconGrid>
            </FeatureBox>
            <FeatureBox>
              <FeatureTitle>
                Deploy once,
                <br />
                configure remotely.
              </FeatureTitle>
              <FeatureText>
                Seamlessly collect and block events relevant to your business.
              </FeatureText>
            </FeatureBox>
            <FeatureBox>
              <FeatureTitle>
                Optimized for Minimal
                <br />
                CPU Impact
              </FeatureTitle>
              <FeatureText>Ensures smooth operation without slowing down your app.</FeatureText>
            </FeatureBox>
          </FeaturesGrid>
          <DoNotShowButtonWrapper onClick={handleCheckboxClick}>
            <Checkbox checked={isChecked}>
              <Popconfirm
                title={
                  <span
                    style={{
                      color: '#4C4C4C',
                      fontWeight: 500,
                    }}
                  >
                    Are you sure you want to skip integration process?
                  </span>
                }
                icon={null}
                placement="bottom"
                open={popOpen}
                onOpenChange={handlePopOpenChange}
                onConfirm={handleNotShowClick}
                onCancel={handlePopCancel}
                okText="Skip Now"
                cancelText="Go Back"
                okButtonProps={{
                  danger: true,
                  ghost: true,
                  style: {
                    fontWeight: 500,
                    padding: '0 12px',
                  },
                }}
                cancelButtonProps={{
                  style: {
                    fontWeight: 500,
                    borderColor: '#4C4C4C',
                    color: '#4C4C4C',
                    padding: '0 12px',
                  },
                }}
                getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
              >
                <DoNotShowButton>Skip the welcome page</DoNotShowButton>
              </Popconfirm>
            </Checkbox>
          </DoNotShowButtonWrapper>
        </ProductHighlights>
      </Content>

      <StyledDrawer
        title="Invite New User"
        placement="right"
        closable
        onClose={onCloseInvite}
        open={visible}
        key="invite_drawer"
        getContainer={false}
        mask
        width={420}
        destroyOnHidden={true}
      >
        <UserProfileEditor action="invite" onCreate={requestInviteUsers} onCancel={onCloseInvite} />
      </StyledDrawer>
    </Container>
  );
}

export default Welcome;
